import { useMemo, useState, useEffect, useRef } from 'react';
import { useGameState } from '../GameProvider';
import PlayerSeat from './PlayerSeat';
import ScoreHistory from './ScoreHistory';
import ShowdownReplayModal from './ShowdownReplayModal';
import './PokerTable.css';

// ── Seat positions: derived from the felt ellipse, not hand-placed presets ──
const TABLE_W = 700;
const TABLE_H = 438;
const CX = TABLE_W / 2;
const CY = TABLE_H / 2;

function polarToSeat(angleDeg, rx, ry) {
  const theta = (angleDeg * Math.PI) / 180;
  const x = CX + (rx * Math.cos(theta));
  const y = CY + (ry * Math.sin(theta));
  return {
    left: `${(x / TABLE_W) * 100}%`,
    top: `${(y / TABLE_H) * 100}%`,
    angleDeg,
  };
}

function getSeatAngles(count) {
  const layouts = {
    1: [90],
    2: [90, 270],
    3: [90, 210, 330],
    4: [90, 180, 270, 0],
    5: [90, 150, 210, 330, 30],
    6: [90, 140, 190, 270, 350, 40],
    7: [90, 130, 170, 220, 320, 10, 50],
    8: [90, 125, 160, 215, 270, 325, 20, 55],
  };
  return layouts[count] || layouts[8];
}

function getSeatPositions(count, isMobile = false) {
  if (count === 0) return [];

  const angles = getSeatAngles(count);
  // Match the felt/rail ellipse so avatars sit on the table ring instead of
  // drifting toward the center.
  const rx = isMobile ? 315 : 330;
  const ry = isMobile ? 188 : 200;

  return angles.map((angle) => polarToSeat(angle, rx, ry));
}

function reorderForMe(playerUids, myUid) {
  const myIdx = playerUids.indexOf(myUid);
  if (myIdx <= 0) return playerUids;
  return [...playerUids.slice(myIdx), ...playerUids.slice(0, myIdx)];
}

// SVG felt oval — identical to score tracker's PokerTableSVG
function FeltSVG() {
  return (
    <svg viewBox={`0 0 ${TABLE_W} ${TABLE_H}`} width="100%" height="100%"
         style={{ position: 'absolute', inset: 0 }}>
      <defs>
        <radialGradient id="gpt-feltGrad" cx="50%" cy="50%" r="60%">
          <stop offset="0%"   stopColor="#1a4d2e" />
          <stop offset="70%"  stopColor="#0f3321" />
          <stop offset="100%" stopColor="#0a2418" />
        </radialGradient>
        <radialGradient id="gpt-glowGrad" cx="50%" cy="50%" r="50%">
          <stop offset="0%"   stopColor="rgba(201,168,76,0.06)" />
          <stop offset="100%" stopColor="transparent" />
        </radialGradient>
      </defs>
      {/* Outer rail */}
      <ellipse cx={CX} cy={CY} rx={CX - 16} ry={CY - 16} fill="none" stroke="#3d2b1a" strokeWidth="18" />
      <ellipse cx={CX} cy={CY} rx={CX - 24} ry={CY - 24} fill="none" stroke="#c9a84c" strokeWidth="1.5" opacity="0.6" />
      {/* Felt */}
      <ellipse cx={CX} cy={CY} rx={CX - 30} ry={CY - 30} fill="url(#gpt-feltGrad)" />
      <ellipse cx={CX} cy={CY} rx={(CX - 30) * 0.7} ry={(CY - 30) * 0.7} fill="url(#gpt-glowGrad)" />
      <ellipse cx={CX} cy={CY} rx={CX - 30} ry={CY - 30} fill="none" stroke="#c9a84c" strokeWidth="1" opacity="0.3" />
      {/* Watermark */}
      <text x={CX} y={CY + 24} textAnchor="middle" fontSize="70" fontFamily="Cinzel, serif"
            fontWeight="700" fill="#c9a84c" opacity="0.1" letterSpacing="14">XAP XAM</text>
    </svg>
  );
}

export default function PokerTable({ children, onPlayerTap, onShowRules, onLeaveRoom, onExitToMenu, onSitOut, onTransferDealer, onKickPlayer, onUnkickPlayer }) {
  const state = useGameState();
  const {
    gameId, uid, phase, players, dealerUid, gameMode,
    readyStatus, revealedArrangements, roundResults, showdownStep,
    betweenRoundsReady, sessionTotals, activeRules,
  } = state;
  const autoWinEnabled = !!activeRules?.autoWinEnabled;

  const playerUids = useMemo(() =>
    Object.entries(players).filter(([, p]) => !p.kicked).map(([id]) => id),
  [players]);

  const orderedUids = useMemo(() => reorderForMe(playerUids, uid), [playerUids, uid]);
  const [isMobileLayout, setIsMobileLayout] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= 768;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onResize = () => setIsMobileLayout(window.innerWidth <= 768);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const seatPositions = useMemo(() => getSeatPositions(orderedUids.length, isMobileLayout), [orderedUids.length, isMobileLayout]);

  const roundhousePlayers = useMemo(() => {
    if (!roundResults || phase !== 'results') return new Set();
    const rh = new Set();
    for (const [pUid, result] of Object.entries(roundResults)) {
      if (result.sweep) rh.add(pUid);
    }
    return rh;
  }, [roundResults, phase]);

  const [showMenu, setShowMenu] = useState(false);
  const [showTransferPicker, setShowTransferPicker] = useState(false);
  const [showManagePicker, setShowManagePicker] = useState(false);
  const [replayRound, setReplayRound] = useState(null);
  const [dealerNotification, setDealerNotification] = useState(null);
  const prevDealerRef = useRef(null);   // last dealer we saw (null until first sync)
  const lastDealerToastRef = useRef(0); // spam-guard against role flapping
  useEffect(() => {
    const prev = prevDealerRef.current;
    prevDealerRef.current = dealerUid;

    // Only toast on a GENUINE hand-off of the dealer role TO this client:
    //  - both ids are known and I am the dealer now,
    //  - there was a real *previous* dealer and it was someone else
    //    (this also suppresses the initial null→dealer sync, so a fresh
    //     joiner or creator never sees it on load).
    if (!dealerUid || !uid || dealerUid !== uid) return;
    if (!prev || prev === uid || prev === dealerUid) return;

    // If the dealer role is flapping, don't spam the toast.
    const now = (typeof performance !== 'undefined' ? performance.now() : 0);
    if (now - lastDealerToastRef.current < 20000) return;
    lastDealerToastRef.current = now;

    setDealerNotification('You are now the Dealer!');
    const t = setTimeout(() => setDealerNotification(null), 4000);
    return () => clearTimeout(t);
  }, [dealerUid, uid]);

  const [copied, setCopied] = useState(false);
  const copyRoomCode = () => {
    if (gameId) {
      navigator.clipboard?.writeText(gameId);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const isDealer = uid === dealerUid;
  const myPlayer = players[uid];
  const amSittingOut = myPlayer?.sittingOut;
  const amSitOutQueued = myPlayer?.sitOutQueued;
  const modeLabels = { basic: 'BASIC', nem: 'NEM', chi: 'CHI', nemchi: 'NEM CHI' };

  const otherActivePlayers = useMemo(() =>
    Object.entries(players)
      .filter(([id, p]) => id !== uid && !p.kicked && !p.sittingOut)
      .map(([id, p]) => ({ uid: id, displayName: p.displayName })),
  [players, uid]);

  // Kickable = everyone but me who isn't already kicked (sitting-out included)
  const otherPlayers = useMemo(() =>
    Object.entries(players)
      .filter(([id, p]) => id !== uid && !p.kicked)
      .map(([id, p]) => ({ uid: id, displayName: p.displayName })),
  [players, uid]);

  // Kicked players the dealer can re-admit (un-ban)
  const kickedPlayers = useMemo(() =>
    Object.entries(players)
      .filter(([id, p]) => id !== uid && p.kicked)
      .map(([id, p]) => ({ uid: id, displayName: p.displayName })),
  [players, uid]);

  // Session totals sorted for the scoreboard strip (highest first)
  const scoreboardPlayers = useMemo(() =>
    orderedUids.map(pUid => ({
      uid: pUid,
      name: players[pUid]?.displayName || 'Player',
      total: sessionTotals[pUid] || 0,
      roundScore: roundResults?.[pUid]?.total,
    })),
  [orderedUids, players, sessionTotals, roundResults]);

  const scoreColor = (n) => {
    if (!n || n === 0) return 'var(--score-neutral)';
    return n > 0 ? 'var(--score-positive)' : 'var(--score-negative)';
  };

  return (
    <div
      className="gpt-wrapper"
      data-testid="poker-table"
      data-room-code={gameId || ''}
      data-phase={phase || ''}
      data-is-dealer={isDealer ? '1' : '0'}
    >

      {/* ── HUD bar ── */}
      <div className="gpt-hud">
        <span className="gpt-hud__mode-pill" data-testid="hud-mode">{modeLabels[gameMode] || gameMode}</span>
        <span
          className="gpt-hud__room-code"
          data-testid="hud-room-code"
          onClick={copyRoomCode}
          title="Click to copy"
        >
          {gameId} {copied && <span className="gpt-hud__copied">Copied!</span>}
        </span>
        <div className="gpt-hud__menu-container">
          <button className="gpt-hud__menu-btn" onClick={() => setShowMenu(!showMenu)}>&#9776;</button>
          {showMenu && (
            <div className="gpt-hud__dropdown">
              {onShowRules && (
                <button className="gpt-hud__dropdown-item" onClick={() => { onShowRules(); setShowMenu(false); }}>Rules</button>
              )}
              <button className="gpt-hud__dropdown-item" onClick={() => {
                if (isDealer && !amSittingOut) { setShowTransferPicker(true); }
                else { onSitOut?.(); }
                setShowMenu(false);
              }}>
                {amSittingOut ? 'Rejoin Game' : amSitOutQueued ? 'Cancel Sit Out' : 'Sit Out'}
              </button>
              {isDealer && onKickPlayer && (
                <button className="gpt-hud__dropdown-item" data-testid="manage-players"
                        onClick={() => { setShowManagePicker(true); setShowMenu(false); }}>
                  Manage Players
                </button>
              )}
              <button className="gpt-hud__dropdown-item gpt-hud__dropdown-item--danger"
                      onClick={() => { onLeaveRoom(); setShowMenu(false); }}>
                Leave Room
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Scrollable content ── */}
      <div className="gpt-scroll" data-layout={isMobileLayout ? 'mobile' : 'desktop'} data-player-count={orderedUids.length}>

        {/* ── Oval table ── */}
        <div className="gpt-table-area">
          <FeltSVG />

          {/* Center content (dealer controls / dealing animation) */}
          <div className="gpt-center">
            {phase === 'dealing' ? (
              <div className="gpt-dealing">
                <div className="gpt-dealing__cards">
                  <div className="gpt-dealing__card gpt-dealing__card--1" />
                  <div className="gpt-dealing__card gpt-dealing__card--2" />
                  <div className="gpt-dealing__card gpt-dealing__card--3" />
                </div>
                <span className="gpt-dealing__text">Dealing...</span>
              </div>
            ) : children}
          </div>

          {/* Player seats around the rim */}
          {orderedUids.map((pUid, i) => {
            const pos = seatPositions[i];
            if (!pos) return null;
            return (
              <div key={pUid} className="gpt-seat" style={{ left: pos.left, top: pos.top }}>
                <PlayerSeat
                  player={players[pUid]}
                  uid={pUid}
                  isDealer={pUid === dealerUid}
                  isReady={
                    phase === 'lobby'   ? !!readyStatus[pUid] :
                    phase === 'results' ? !!betweenRoundsReady[pUid] : false
                  }
                  isMe={pUid === uid}
                  phase={phase}
                  arrangement={players[pUid]?.sittingOut ? null : revealedArrangements?.[pUid]}
                  score={roundResults?.[pUid]?.total}
                  showdownStep={showdownStep}
                  gameMode={gameMode}
                  roundhouseAll={roundhousePlayers.has(pUid)}
                  autoWinEnabled={autoWinEnabled}
                  onTap={onPlayerTap}
                  rotate={0}
                  avatarBelow={parseFloat(pos.top) >= 50}
                  sessionTotal={sessionTotals[pUid] || 0}
                  rowScores={(() => {
                    const br = roundResults?.[pUid]?.breakdown;
                    if (!br) return null;
                    let bottom = 0, middle = 0, top = 0;
                    for (const opp of Object.values(br)) {
                      const src = opp.rowPoints || opp.rows;
                      if (src) { bottom += src[0]||0; middle += src[1]||0; top += src[2]||0; }
                    }
                    return { bottom, middle, top };
                  })()}
                />
              </div>
            );
          })}
        </div>

        {/* ── Session scoreboard ── */}
        {scoreboardPlayers.length > 0 && (
          <div className="gpt-scoreboard">
            {scoreboardPlayers.map(({ uid: pUid, name, total, roundScore }) => (
              <div key={pUid} className="gpt-scoreboard__player">
                <span className="gpt-scoreboard__name">{name}</span>
                {roundScore !== undefined && (
                  <span className="gpt-scoreboard__round" style={{ color: scoreColor(roundScore) }}>
                    {roundScore > 0 ? `+${roundScore}` : roundScore}
                  </span>
                )}
                <span className="gpt-scoreboard__total" style={{ color: scoreColor(total) }}>
                  {total > 0 ? `+${total}` : total}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* ── Running score history (click a round to replay its showdown) ── */}
        <ScoreHistory onSelectRound={setReplayRound} />

      </div>{/* /gpt-scroll */}

      {/* ── Toasts & modals ── */}
      {dealerNotification && (
        <div className="gpt-toast">{dealerNotification}</div>
      )}

      {replayRound && (
        <ShowdownReplayModal round={replayRound} onClose={() => setReplayRound(null)} />
      )}

      {showManagePicker && (
        <div className="gpt-transfer-overlay" onClick={() => setShowManagePicker(false)}>
          <div className="gpt-transfer-modal" onClick={e => e.stopPropagation()}>
            <h3>Manage Players</h3>
            {otherPlayers.length === 0 && kickedPlayers.length === 0
              ? <p className="gpt-transfer-hint">No other players in the room.</p>
              : (
                <div className="gpt-transfer-list">
                  {otherPlayers.map(p => (
                    <div key={p.uid} className="gpt-manage-row">
                      <span className="gpt-manage-row__name">{p.displayName}</span>
                      <button
                        className="gpt-manage-row__kick"
                        data-testid={`kick-${p.displayName}`}
                        onClick={() => onKickPlayer?.(p.uid)}
                      >
                        Kick
                      </button>
                    </div>
                  ))}
                  {kickedPlayers.map(p => (
                    <div key={p.uid} className="gpt-manage-row">
                      <span className="gpt-manage-row__name gpt-manage-row__name--kicked">{p.displayName} · kicked</span>
                      <button
                        className="gpt-manage-row__allow"
                        data-testid={`allow-${p.displayName}`}
                        onClick={() => onUnkickPlayer?.(p.uid)}
                      >
                        Allow back
                      </button>
                    </div>
                  ))}
                </div>
              )
            }
            <button className="btn btn--secondary" onClick={() => setShowManagePicker(false)}>Close</button>
          </div>
        </div>
      )}

      {showTransferPicker && (
        <div className="gpt-transfer-overlay" onClick={() => setShowTransferPicker(false)}>
          <div className="gpt-transfer-modal" onClick={e => e.stopPropagation()}>
            <h3>Transfer Dealer to:</h3>
            <p className="gpt-transfer-hint">You must hand off the dealer role before sitting out.</p>
            {otherActivePlayers.length === 0
              ? <p className="gpt-transfer-hint">No other active players.</p>
              : (
                <div className="gpt-transfer-list">
                  {otherActivePlayers.map(p => (
                    <button key={p.uid} className="gpt-transfer-player" onClick={() => {
                      onTransferDealer?.(p.uid);
                      setShowTransferPicker(false);
                      setTimeout(() => onSitOut?.(), 500);
                    }}>{p.displayName}</button>
                  ))}
                </div>
              )
            }
            <button className="btn btn--secondary" onClick={() => setShowTransferPicker(false)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
